// src/pages/public-access/DocumentViewerPage.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import {
  Download,
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Loader2,
  Eye,
  Calendar,
  User,
  Building,
  PaperclipIcon,
  X,
  RefreshCw,
  FileSignature
} from 'lucide-react';
import toast from 'react-hot-toast';
import ContractPDFViewer from '../contracts/components/ContractPDFViewer';
import SignedDocumentUpload from '../../components/contracts/SignedDocumentUpload';
import UploadedDocumentsViewer from '../../components/contracts/UploadedDocumentsViewer';
import AdditionalActModal from '../../components/additional-acts/AdditionalActModal';
import TerminationNoticeModal from '../../components/notices/TerminationNoticeModal';
import CompactDocumentUpload from '../../components/contracts/CompactDocumentUpload';

// Import your API service - adjust path as needed
import { contractAccessService } from '../../services/';

const DocumentViewerPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [showPDFViewer, setShowPDFViewer] = useState(true);
  const [refreshDocuments, setRefreshDocuments] = useState(0);
  const [activeTab, setActiveTab] = useState('upload');


  const [selectedAct, setSelectedAct] = useState(null);
  const [showActModal, setShowActModal] = useState(false);

  const [selectedNotice, setSelectedNotice] = useState(null);
  const [showNoticeModal, setShowNoticeModal] = useState(false);

  const [expandedActId, setExpandedActId] = useState(null);
  const [expandedNoticeId, setExpandedNoticeId] = useState(null);

  const location = useLocation();
  const contractIdFromState = location.state?.contractId;

  // Fetch contract information
  const {
    data: contractInfo,
    isLoading: contractLoading,
    error: contractError,
    refetch: refetchContract
  } = useQuery(
    ['contractByToken', token, contractIdFromState], // ✨ Adaugă contractId în key
    () => contractAccessService.getContractByToken(token, contractIdFromState), // ✨ Trimite contractId
    {
      enabled: !!token,
      onSuccess: (data) => {
        console.log('Contract data:', data);
        console.log('Additional acts:', data.additionalActs);

        if (data?.pdfFilePath) {
          setShowPDFViewer(true);
          console.log('a mers', showPDFViewer);
        }
        else {
          setShowPDFViewer(false);
          console.log("a dat fail", showPDFViewer);
        }
      },
      onError: (error) => {
        console.error('Error fetching contract:', error);
        if (error.response?.status === 401) {
          toast.error('Sesiunea a expirat. Vă rugăm să vă autentificați din nou.');
          navigate(`/contract-access/${token}`);
        } else {
          toast.error('Eroare la încărcarea contractului');
        }
      }
    }
  );

  // ✨ NOU - Handler pentru vizualizare act
  const handleViewAct = (act) => {
    setSelectedAct(act);
    setShowActModal(true);
  };

  // ✨ NOU - Handler pentru download act
  const handleDownloadAct = async (act) => {
    try {
      const blob = await contractAccessService.downloadAdditionalAct(token, act.id);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Act_Aditional_${act.number}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Act adițional descărcat cu succes!');
    } catch (error) {
      console.error('Error downloading additional act:', error);
      toast.error('Eroare la descărcarea actului adițional');
    }
  };

  // ✨ NOU - Handler pentru închidere modal
  const handleCloseActModal = () => {
    setSelectedAct(null);
    setShowActModal(false);
    refetchContract(); // Refresh pentru status update
  };


  // ✨ NOU - Handler pentru vizualizare Reziliere
  const handleViewNotice = (notice) => {
    setSelectedNotice(notice);
    setShowNoticeModal(true);
  };

  // ✨ NOU - Handler pentru download Reziliere
  const handleDownloadNotice = async (notice) => {
    try {
      // contractAccessService.downloadNotice returns a Blob - **ATENȚIE**: Trebuie să existe un endpoint corespunzător!
      const blob = await contractAccessService.downloadNotice(token, notice.id);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Notificare_Reziliere_${notice.id}.pdf`; // Folosim ID-ul ca referință
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Notificare descărcată cu succes!');
    } catch (error) {
      console.error('Error downloading notice:', error);
      toast.error('Eroare la descărcarea notificării');
    }
  };

  // ✨ NOU - Handler pentru închidere modal Reziliere
  const handleCloseNoticeModal = () => {
    setSelectedNotice(null);
    setShowNoticeModal(false);
    refetchContract(); // Refresh pentru status update
  };

  const handleDownload = async () => {
    try {
      // contractAccessService.downloadContract returns a Blob
      const blob = await contractAccessService.downloadContract(token);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Contract_${contractInfo?.contractNumber || 'document'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Document descărcat cu succes!');
    } catch (error) {
      console.error('Error downloading contract:', error);
      toast.error('Eroare la descărcarea documentului');
    }
  };

  const handleUploadSuccess = (data) => {
    console.log('Upload successful:', data);
    toast.success('Documente încărcate cu succes!');
    refetchContract();

    setRefreshDocuments(prev => prev + 1);
  };


  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('ro-RO', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Data invalidă';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Signed':
      case 'Semnat':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-4 h-4 mr-1" />
            Semnat
          </span>
        );
      case 'Accessed':
      case 'Accesat':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            <Eye className="w-4 h-4 mr-1" />
            Accesat
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
            <FileText className="w-4 h-4 mr-1" />
            {status || 'În așteptare'}
          </span>
        );
    }
  };

  if (contractLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-primary-500 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Se încarcă contractul...
            </h2>
            <p className="text-gray-600">
              Vă rugăm să așteptați
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (contractError || !contractInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Eroare de Acces
            </h2>
            <p className="text-gray-600 mb-6">
              Nu s-a putut încărca contractul. Vă rugăm să încercați din nou.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => refetchContract()}
                className="w-full bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors flex items-center justify-center"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Încearcă din nou
              </button>
              <button
                onClick={() => navigate(`/contract-access/${token}`)}
                className="w-full bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Înapoi la verificare
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => {
                    // Dacă am venit din dashboard, mergem înapoi la dashboard
                    // Altfel mergem la verificarea codului
                    if (location.state?.fromDashboard || contractIdFromState) {
                      navigate(`/contract-access/${token}/dashboard`);
                    } else {
                      navigate(`/contract-access/${token}`);
                    }
                  }}
                  className="text-gray-500 hover:text-gray-700 transition-colors flex items-center"
                >
                  <ArrowLeft className="w-6 h-6" />
                  <span className="ml-2 text-sm hidden sm:inline">
                    {contractIdFromState ? 'Înapoi la contracte' : 'Înapoi'}
                  </span>
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Contract {contractInfo.contractNumber}
                  </h1>
                  <p className="text-sm text-gray-600">
                    Vizualizare și semnare document
                  </p>
                </div>
              </div>
              <div>
                {getStatusBadge(contractInfo.status)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Document Actions (Sidebar) */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
              {/* Contract Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Informații Contract
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center text-sm">
                    <Building className="w-4 h-4 text-gray-500 mr-2" />
                    <span className="text-gray-600">Client:</span>
                    <span className="ml-2 font-medium">{contractInfo.clientName}</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <FileText className="w-4 h-4 text-gray-500 mr-2" />
                    <span className="text-gray-600">Număr:</span>
                    <span className="ml-2 font-medium">{contractInfo.contractNumber}</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <Calendar className="w-4 h-4 text-gray-500 mr-2" />
                    <span className="text-gray-600">Status:</span>
                    <span className="ml-2">{contractInfo.status}</span>
                  </div>
                </div>
              </div>

              {/* Download Section */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Descărcare Document</h4>
                <button
                  onClick={handleDownload}
                  className="w-full bg-primary-500 text-white py-3 px-4 rounded-lg hover:bg-primary-600 transition-colors flex items-center justify-center"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Descarcă PDF
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  Descărcați documentul pentru a-l vedea și semna
                </p>
              </div>

              {/* Status Info */}
              {contractInfo.status === 'Signed' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-green-800">
                        Contract Semnat
                      </p>
                      <p className="text-xs text-green-600">
                        Documentul a fost semnat și procesat cu succes
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Instructions */}
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Instrucțiuni pentru semnare:</h4>
                <ol className="text-sm text-blue-800 space-y-1">
                  <li>1. Descărcați documentul PDF folosind butonul de mai sus</li>
                  <li>2. Deschideți documentul și semnați-l digital sau printați și semnați manual</li>
                  <li>3. Dacă ați semnat manual, scanați documentul în format PDF</li>
                  <li>4. Încărcați documentul semnat folosind secțiunea din stânga/laterală</li>
                </ol>
              </div>

            </div>
          </div>

          {/* PDF Viewer Placeholder (lg:col-span-2) */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Previzualizare Document
                </h3>
                <button
                  onClick={handleDownload}
                  className="text-primary-500 hover:text-primary-600 text-sm flex items-center"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Descarcă
                </button>
              </div>

              {/* PDF Viewer Placeholder */}
              <div className="border border-gray-200 rounded-lg h-96 flex items-center justify-center bg-gray-50">
                <div className="w-full h-full text-center relative">
                  {showPDFViewer && contractInfo?.pdfFilePath ? (
                    <div className="absolute inset-0 rounded-lg overflow-hidden">
                      <ContractPDFViewer
                        contract={contractInfo}
                        pdfUrl={`/api/contract-access/${token}/download`}
                        onClose={() => setShowPDFViewer(false)}
                        onDownload={handleDownload}
                        isPublic={false}
                        showHeader={false}
                      />
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 font-medium mb-2">
                        Previzualizare PDF
                      </p>
                      <p className="text-sm text-gray-500 mb-4">
                        Pentru a vedea conținutul complet al contractului,<br />
                        vă rugăm să descărcați documentul PDF
                      </p>
                      <button
                        onClick={handleDownload}
                        className="bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors inline-flex items-center"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Descarcă PDF
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-8 bg-white rounded-lg shadow-md">

                {/* Navigare Tab-uri */}
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex space-x-8 px-6 pt-4" aria-label="Tabs">
                    <button
                      onClick={() => setActiveTab('upload')}
                      className={`
                    ${activeTab === 'upload'
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }
                    whitespace-nowrap py-3 font-medium text-sm border-b-2 transition-colors duration-200 focus:outline-none
                `}
                    >
                      <Upload className="w-4 h-4 inline mr-2" />
                      Încărcare Document Semnat
                    </button>
                    <button
                      onClick={() => setActiveTab('documents')}
                      className={`
                    ${activeTab === 'documents'
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }
                    whitespace-nowrap py-3 font-medium text-sm border-b-2 transition-colors duration-200 focus:outline-none
                `}
                    >
                      <PaperclipIcon className="w-4 h-4 inline mr-2" />
                      Documente Încărcate
                    </button>
                  </nav>
                </div>

                {/* Conținutul Tab-ului */}
                <div className="p-6">
                  {/* Tab-ul: Încărcare Document Semnat */}
                  {activeTab === 'upload' && (
                    <div className="space-y-6">
                      {/* Componenta de Upload */}
                      <SignedDocumentUpload
                        token={token}
                        uploadType="contract"
                        entityLabel={`Contract ${contractInfo.contractNumber}`}
                        onUploadSuccess={handleUploadSuccess}
                      />
                    </div>
                  )}

                  {/* Tab-ul: Documente Încărcate */}
                  {activeTab === 'documents' && (
                    <div>
                      <UploadedDocumentsViewer
                        token={token}
                        key={refreshDocuments} // Forțează reîncărcarea când sunt uploadate documente noi
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* ✨ NOU - Termination Notices Section */}
        {contractInfo?.notices && contractInfo.notices.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                  Notificări de Reziliere
                </h2>
                <span className="text-sm text-gray-600">
                  {contractInfo.notices.length} notificare(i)
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {contractInfo.notices.map((notice) => (
                  <div
                    key={notice.id}
                    className="border border-gray-200 rounded-lg overflow-hidden hover:border-red-500 hover:shadow-md transition-all"
                  >
                    {/* Card Header */}
                    <div
                      className="p-4 cursor-pointer bg-white hover:bg-gray-50 transition-colors"
                      onClick={() => handleViewNotice(notice)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                          <h3 className="font-semibold text-gray-900">
                            Notificare #{notice.id}
                          </h3>
                        </div>
                        {notice.hasSignedDocuments && (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        )}
                      </div>

                      {/* Date */}
                      <div className="flex items-center text-sm text-gray-600 mb-3">
                        <Calendar className="w-4 h-4 mr-2" />
                        {formatDate(notice.date)}
                      </div>

                      {/* Status Badge */}
                      <div className="flex items-center gap-2">
                        {notice.hasSignedDocuments ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Confirmat
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            În așteptare
                          </span>
                        )}
                      </div>

                      {/* Actions Row */}
                      <div className="mt-4 flex gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadNotice(notice);
                          }}
                          className="flex-1 text-sm px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-1"
                        >
                          <Download className="w-4 h-4" />
                          Descarcă
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewNotice(notice);
                          }}
                          className="flex-1 text-sm px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          Vezi
                        </button>
                      </div>
                    </div>

                    {/* Expandable Upload Section */}
                    <div className="border-t border-gray-200">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedNoticeId(expandedNoticeId === notice.id ? null : notice.id);
                        }}
                        className="w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-between"
                      >
                        <span className="flex items-center gap-2">
                          <Upload className="w-4 h-4" />
                          Încarcă documente
                        </span>
                        <span className={`transform transition-transform ${expandedNoticeId === notice.id ? 'rotate-180' : ''}`}>
                          ▼
                        </span>
                      </button>

                      {/* Upload Component (Expanded) */}
                      {expandedNoticeId === notice.id && (
                        <div className="px-4 pb-4 bg-gray-50">
                          <CompactDocumentUpload
                            token={token}
                            uploadType="notice"
                            entityId={notice.id}
                            buttonText="Selectează fișiere"
                            buttonVariant="danger"
                            onUploadSuccess={(data) => {
                              console.log('Upload success:', data);
                              toast.success('Documente încărcate!');
                              refetchContract();
                              setExpandedNoticeId(null);
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ✨ NOU - Additional Acts Section */}
        {contractInfo?.additionalActs && contractInfo.additionalActs.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <FileSignature className="w-6 h-6 text-primary-600" />
                  Acte Adiționale
                </h2>
                <span className="text-sm text-gray-600">
                  {contractInfo.additionalActs.length} act(e)
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {contractInfo.additionalActs.map((act) => (
                  <div
                    key={act.id}
                    className="border border-gray-200 rounded-lg overflow-hidden hover:border-primary-500 hover:shadow-md transition-all"
                  >
                    {/* Card Header */}
                    <div
                      className="p-4 cursor-pointer bg-white hover:bg-gray-50 transition-colors"
                      onClick={() => handleViewAct(act)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <FileSignature className="w-5 h-5 text-primary-600 flex-shrink-0" />
                          <h3 className="font-semibold text-gray-900">
                            Act {act.number}
                          </h3>
                        </div>
                        {act.hasSignedDocuments && (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        )}
                      </div>

                      {/* Date */}
                      <div className="flex items-center text-sm text-gray-600 mb-3">
                        <Calendar className="w-4 h-4 mr-2" />
                        {formatDate(act.date)}
                      </div>

                      {/* Status Badge */}
                      <div className="flex items-center gap-2">
                        {act.hasSignedDocuments ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Semnat
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            În așteptare
                          </span>
                        )}
                      </div>

                      {/* Actions Row */}
                      <div className="mt-4 flex gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadAct(act);
                          }}
                          className="flex-1 text-sm px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-1"
                        >
                          <Download className="w-4 h-4" />
                          Descarcă
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewAct(act);
                          }}
                          className="flex-1 text-sm px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          Vezi
                        </button>
                      </div>
                    </div>

                    {/* Expandable Upload Section */}
                    <div className="border-t border-gray-200">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedActId(expandedActId === act.id ? null : act.id);
                        }}
                        className="w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-between"
                      >
                        <span className="flex items-center gap-2">
                          <Upload className="w-4 h-4" />
                          Încarcă documente
                        </span>
                        <span className={`transform transition-transform ${expandedActId === act.id ? 'rotate-180' : ''}`}>
                          ▼
                        </span>
                      </button>

                      {/* Upload Component (Expanded) */}
                      {expandedActId === act.id && (
                        <div className="px-4 pb-4 bg-gray-50">
                          <CompactDocumentUpload
                            token={token}
                            uploadType="additional-act"
                            entityId={act.id}
                            buttonText="Selectează fișiere"
                            buttonVariant="primary"
                            onUploadSuccess={(data) => {
                              console.log('Upload success:', data);
                              toast.success('Documente încărcate!');
                              refetchContract();
                              setExpandedActId(null);
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ✨ NOU - Additional Act Modal */}
        {showActModal && selectedAct && (
          <AdditionalActModal
            act={selectedAct}
            token={token}
            contractInfo={contractInfo}
            onClose={handleCloseActModal}
            onDownload={() => handleDownloadAct(selectedAct)}
          />
        )}
        {/* ✨ NOU - Termination Notice Modal */}
        {showNoticeModal && selectedNotice && (
          <TerminationNoticeModal
            notice={selectedNotice}
            token={token}
            contractInfo={contractInfo}
            onClose={handleCloseNoticeModal}
            onDownload={() => handleDownloadNotice(selectedNotice)}
          />
        )}
      </div>
    </div>
  );
};

export default DocumentViewerPage;